// Selection Manager Hook - Handles client-side payslip selection with dual-mode support
const PayslipsSelectionManager = {
  mounted(this: any) {
    // Initialize selection state
    this.selectedPayslipIds = new Set();
    this.excludedPayslipIds = new Set();
    this.selectAllMode = false;

    // Get configuration from data attributes
    this.selectionContainer =
      this.el.dataset.selectionContainer || "payroll-payslip-table";
    this.actionBarId = this.el.dataset.actionBarId || "payslip-action-bar";
    this.selectAllBtnId =
      this.el.dataset.selectAllBtnId || "toggle-select-all-btn";

    // Attach event listeners
    this.attachEventListeners();

    // Listen for Phoenix events
    this.handleEvent("clear_selections", () => this.clearAllSelections());
    this.handleEvent("action_bar_ready", () =>
      this.showSelectAllLoading(false),
    );
  },

  updated(this: any) {
    this.updateActionBar();
    this.updateCheckboxes();
    this.updateSelectAllButton();
  },

  showSelectAllLoading(this: any, loading: boolean) {
    const btn = document.getElementById(this.selectAllBtnId);
    if (!btn) return;

    if (loading) {
      btn.classList.add("opacity-50", "pointer-events-none");
      const textEl = btn.querySelector("#select-all-btn-text");
      if (textEl) textEl.textContent = "Loading...";

      // Safety timeout in case the action bar event doesn't fire
      this._loadingTimeout = setTimeout(
        () => this.showSelectAllLoading(false),
        5000,
      );
    } else {
      btn.classList.remove("opacity-50", "pointer-events-none");
      this.updateSelectAllButton();
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
        this._loadingTimeout = null;
      }
    }
  },

  attachEventListeners(this: any) {
    document.addEventListener("click", (event: any) => {
      // Listen for select all button clicks (button or its children)
      const selectAllBtn = event.target.closest(`#${this.selectAllBtnId}`);
      if (selectAllBtn) {
        this.handleSelectAll();
        return;
      }

      // Listen for individual payslip checkbox clicks
      if (
        event.target.matches(
          '[id^="toggle-"]:not([id="' + this.selectAllBtnId + '"])',
        )
      ) {
        const payslipId = event.target.getAttribute("phx-value-id");
        if (payslipId) {
          this.handleTogglePayslip(payslipId, event.target.checked);
        }
      }

      return;
    });
  },

  handleSelectAll(this: any) {
    const wasInSelectAllWithNoExclusions =
      this.selectAllMode && this.excludedPayslipIds.size === 0;

    if (wasInSelectAllWithNoExclusions) {
      // If everything was selected, toggle to select none.
      this.clearAllSelections();
    } else {
      // Otherwise, enter 'select all' mode and clear any individual selections/exclusions.
      this.selectAllMode = true;
      this.selectedPayslipIds.clear();
      this.excludedPayslipIds.clear();
      this.updateCheckboxes();
      this.updateSelectAllButton();
    }

    this.showSelectAllLoading(true);
    this.updateActionBar();
  },

  handleTogglePayslip(this: any, payslipId: string, isChecked: boolean) {
    if (this.selectAllMode) {
      if (isChecked) {
        // Remove from excluded list (making it selected)
        this.excludedPayslipIds.delete(payslipId);
      } else {
        // Add to excluded list (making it unselected)
        this.excludedPayslipIds.add(payslipId);
      }
      this.updateSelectAllButton();
    } else {
      if (isChecked) {
        // Add to selected list
        this.selectedPayslipIds.add(payslipId);
      } else {
        // Remove from selected list
        this.selectedPayslipIds.delete(payslipId);
      }
    }

    this.updateActionBar();
  },

  clearAllSelections(this: any) {
    this.selectAllMode = false;
    this.selectedPayslipIds.clear();
    this.excludedPayslipIds.clear();
    this.updateCheckboxes();
    this.updateSelectAllButton();
  },

  updateCheckboxes(this: any) {
    // Update individual payslip checkboxes based on current state
    const table = document.getElementById(this.selectionContainer);
    if (!table) return;

    const checkboxes = table.querySelectorAll(
      '[id^="toggle-"]:not([id="' + this.selectAllBtnId + '"])',
    );
    checkboxes.forEach((checkbox: any) => {
      const payslipId = checkbox.getAttribute("phx-value-id");
      if (!payslipId) return;

      let isSelected = false;
      if (this.selectAllMode) {
        // In select-all mode: selected unless excluded
        isSelected = !this.excludedPayslipIds.has(payslipId);
      } else {
        // In normal mode: selected if in selected list
        isSelected = this.selectedPayslipIds.has(payslipId);
      }

      checkbox.checked = isSelected;
    });
  },

  updateSelectAllButton(this: any) {
    const btn = document.getElementById(this.selectAllBtnId);
    if (!btn) return;

    const textEl = btn.querySelector("#select-all-btn-text");
    if (!textEl) return;

    const isAllSelected =
      this.selectAllMode && this.excludedPayslipIds.size === 0;

    if (isAllSelected) {
      textEl.textContent = "Deselect All Payslips";
      btn.classList.add("bulk-select-active");
    } else {
      const originalText = btn.dataset.originalText;
      if (originalText) {
        textEl.textContent = originalText;
      }
      btn.classList.remove("bulk-select-active");
    }
  },

  updateActionBar(this: any) {
    const hasSelections = this.hasSelectedPayslips();
    const selectionData = this.getSelectionData();

    // Send selection data directly to the ActionBar component to avoid main component rerenders
    if (document.querySelector(`#${this.actionBarId}`))
      this.pushEventTo(`#${this.actionBarId}`, "selection_updated", {
        has_selections: hasSelections,
        selection_data: selectionData,
      });
  },

  hideActionBar(this: any) {
    if (document.querySelector(`#${this.actionBarId}`))
      this.pushEventTo(`#${this.actionBarId}`, "hide", {});
  },

  hasSelectedPayslips(this: any) {
    if (this.selectAllMode) {
      // In select-all mode, we have selections unless all are excluded
      // For simplicity, assume we have selections if in select-all mode
      return true;
    } else {
      return this.selectedPayslipIds.size > 0;
    }
  },

  getSelectionData(this: any) {
    return {
      select_all_mode: this.selectAllMode,
      selected_payslip_ids: Array.from(this.selectedPayslipIds),
      excluded_payslip_ids: Array.from(this.excludedPayslipIds),
    };
  },
};

export default PayslipsSelectionManager;
